import type { FieldValidator } from 'final-form';

import type { KycCompanyDetails } from 'Services/motorway/services/kyc/kyc.types';
import type { AssociateType, KycStatus } from 'Utilities/types/kyc.types';

import type { VALIDATION_ERROR } from './AccountSettingsCompanyInformation.consts';

/**
 * Determines which variant of the company information form to display.
 * - 'payment': Used in the standard account-settings flow (/account-settings/company-information).
 *   Includes the Authorised Users section and uses the payment-specific route prefix.
 * - 'authentication': Used in the KYC authentication flow (/account-settings/kyc).
 *   Excludes the Authorised Users section and uses KYC-specific routes and copy.
 */
export type CompanyInformationVariant = 'payment' | 'authentication';

export type CompanyInformationWizardContext = {
	companyDetails: KycCompanyDetails;
	kycStatus: KycStatus;
	variant: CompanyInformationVariant;
};

export type Associate = {
	address?: Address;
	associateId?: string;
	dateOfBirth?: string;
	emailAddress?: string;
	fullName: string;
	isDeleted?: boolean;
	isDraft?: boolean;
	isPrimaryContact?: boolean;
	mobilePhone?: string;
	ownership?: number;
	type: AssociateType;
};

export type Address = {
	addressLine1?: string;
	addressLine2?: string;
	addressLine3?: string;
	city?: string;
	postCode?: string;
};

export type CompanyInformationWizardDetail = {
	showHeader?: boolean;
};

export type CompanyInformationRoutePath =
	| ''
	| 'company-information'
	| 'company-details'
	| 'directors'
	| 'beneficial-owners'
	| 'authorised-users'
	| 'summary'
	| 'kyc-complete'
	| 'kyc-complete-live-pro';

export type CompanyInformationRouteName =
	| 'entry'
	| 'company-details'
	| 'directors'
	| 'beneficial-owners'
	| 'authorised-users'
	| 'summary'
	| 'kyc-complete'
	| 'kyc-complete-live-pro';

export type CompanyInformationBaseRoute = `/company-information`;

export type CompanyInformationAvailableRoutes<P extends CompanyInformationRoutePath> = P extends ''
	? CompanyInformationBaseRoute
	: `${CompanyInformationBaseRoute}/${P}`;

export const COMPANY_INFORMATION_STEP = {
	AUTHORISED_USERS: 'authorised-users',
	BENEFICIAL_OWNERS: 'beneficial-owners',
	COMPANY_DETAILS: 'company-details',
	COMPANY_INFORMATION: 'entry',
	DIRECTORS: 'directors',
	KYC_COMPLETE: 'kyc-complete',
	KYC_COMPLETE_PRO: 'kyc-complete-live-pro',
	SUMMARY: 'summary',
} as const;

export const ASSOCIATE_TYPE = {
	AUTHORISED_USER: 'accountManager',
	BENEFICIAL_OWNER: 'shareholder',
	DIRECTOR: 'director',
} as const;

export type CompanyInformationAssociateType = keyof typeof ASSOCIATE_TYPE;
export type CompanyInformationAssociateValue = (typeof ASSOCIATE_TYPE)[CompanyInformationAssociateType];

export const ASSOCIATE_LEVEL = {
	PRIMARY: 'primary',
	SECONDARY: 'secondary',
} as const;

export type CompanyInformationAssociateLevelKey = keyof typeof ASSOCIATE_LEVEL;
export type CompanyInformationAssociateLevelValue = (typeof ASSOCIATE_LEVEL)[CompanyInformationAssociateLevelKey];

export type AssociateFieldsGroup = {
	displayAddress: boolean;
	displayAuthenticationPhoneContact: boolean;
	displayCompanyInformation: boolean;
	displayEmailContact: boolean;
	displayPersonalInformation: boolean;
	displayPhoneContact: boolean;
};

export type KycSection = {
	id: string;
	percentageCompleted: number;
	text: string;
};

export type KycStepNavigation = {
	navigateTo: {
		[step: string]: () => Promise<boolean>;
	};
};

export type SubmitKycCompanyDetailsFormParams = {
	forcedRoute?: string | null | undefined;
	navigateForward?: boolean;
	values?: Partial<KycCompanyDetails>;
};

export type AddAssociateParams = {
	associate?: Associate;
	draftId: string;
	type?: CompanyInformationAssociateValue;
};

export type SaveAssociateProgressParams = {
	associate?: Partial<Associate>;
};

export type RemoveAssociateParams = {
	associateId?: string;
	isDraft?: boolean;
	isModified?: boolean;
};

export type KycCompanyDetailsFormActions = {
	addDraftAssociate: (type: AddAssociateParams) => void;
	confirmAssociateDelete: (associateParams: RemoveAssociateParams) => void;
	deleteAssociate: (associateParams: RemoveAssociateParams) => void;
	dismissAssociateModal: () => void;
	isDeleteAssociateModalOpen: boolean;
	saveAssociateProgressInWizardState: (associate: SaveAssociateProgressParams) => void;
	submitKycSectionForm: (formParams: SubmitKycCompanyDetailsFormParams) => void;
};

export type CompanyInformationStepKeys = keyof typeof COMPANY_INFORMATION_STEP;

export type CompanyInformationCompanyType =
	| 'privateLimitedCompany'
	| 'publicListedCompany'
	| 'limitedLiabilityPartnership'
	| 'ordinaryPartnership'
	| 'soleTrade';

export type CompanyTypeOption = {
	id: CompanyInformationCompanyType;
	name: string | React.ReactNode;
};

export type AddressFieldsProps = {
	address1: string;
	address2: string;
	address3: string;
	city: string;
	copyField?: {
		fields: {
			origin: string;
			target: string;
		}[];
		id: string;
		label: string | React.ReactNode;
	};
	description?: string | React.ReactNode;
	disabled?: boolean;
	extraContent?: { bottom?: React.ReactNode };
	label: string | React.ReactNode;
	postCode: string;
};

export type AccountSettingCompanyInformationProps = {
	companyDetails?: KycCompanyDetails;
	kycStatus?: KycStatus;
	variant?: CompanyInformationVariant;
};

export type KycStepPath = {
	page: {
		[step: string]: string | undefined;
	};
};

export type AssociateField = {
	address: Required<NonNullable<Address>>;
	dateOfBirth: string;
	emailAddress: string;
	fullName: string;
	mobilePhone: string;
	otpMobilePhone: string;
	ownership: string;
};

export type GroupFieldAllowedSections = Record<
	CompanyInformationAssociateValue,
	Record<CompanyInformationAssociateLevelValue, AssociateFieldsGroup>
>;

export type FormFieldValidator<FieldValue = string, ReturnValue = FormFieldError> = (
	value: Parameters<FieldValidator<FieldValue>>[0],
	allValues?: Parameters<FieldValidator<FieldValue>>[1],
	meta?: Parameters<FieldValidator<FieldValue>>[2],
) => ReturnValue | Promise<ReturnValue>;

export type FormFieldError = { code: AssociateFieldError } | undefined;

type AssociateFieldError = (typeof VALIDATION_ERROR)[keyof typeof VALIDATION_ERROR];
